import { useEffect, useRef } from "react";
import { trackAdminEvent } from "../../../../services/AdminTelemetryService";

const HEARTBEAT_MS = 60_000;

/**
 * Session visibility and duration (visible time only).
 */
export function useEngagement(): void {
	const visibleMsRef = useRef(0);
	const lastVisibleAtRef = useRef<number | null>(null);
	const sessionStartRef = useRef(Date.now());

	useEffect(() => {
		void trackAdminEvent({
			eventType: "engagement",
			level: "info",
			action: "session_start",
			metadata: { at: new Date().toISOString() },
		});

		const tickVisible = () => {
			if (document.visibilityState === "visible") {
				lastVisibleAtRef.current = Date.now();
			}
		};
		tickVisible();

		const onVisibility = () => {
			const now = Date.now();
			if (document.visibilityState === "hidden" && lastVisibleAtRef.current != null) {
				visibleMsRef.current += Math.max(0, now - lastVisibleAtRef.current);
				lastVisibleAtRef.current = null;
			} else if (document.visibilityState === "visible") {
				lastVisibleAtRef.current = now;
			}
		};

		const heartbeat = window.setInterval(() => {
			const now = Date.now();
			let visible = visibleMsRef.current;
			if (document.visibilityState === "visible" && lastVisibleAtRef.current != null) {
				visible += Math.max(0, now - lastVisibleAtRef.current);
				lastVisibleAtRef.current = now;
			}
			void trackAdminEvent({
				eventType: "engagement",
				level: "info",
				action: "session_heartbeat",
				metadata: {
					visibleMsApprox: Math.round(visible),
					sessionMs: now - sessionStartRef.current,
				},
			});
		}, HEARTBEAT_MS);

		const onUnload = () => {
			const now = Date.now();
			let visible = visibleMsRef.current;
			if (document.visibilityState === "visible" && lastVisibleAtRef.current != null) {
				visible += Math.max(0, now - lastVisibleAtRef.current);
			}
			void trackAdminEvent({
				eventType: "engagement",
				level: "info",
				action: "session_end",
				metadata: {
					visibleMsApprox: Math.round(visible),
					sessionMs: now - sessionStartRef.current,
				},
			});
		};

		document.addEventListener("visibilitychange", onVisibility);
		window.addEventListener("beforeunload", onUnload);

		return () => {
			window.clearInterval(heartbeat);
			document.removeEventListener("visibilitychange", onVisibility);
			window.removeEventListener("beforeunload", onUnload);
			onUnload();
		};
	}, []);
}
